import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";

import { Select, FormGroup, Input } from "@cofano/cck";

import { convertedIcon } from "components/icon/IconUtils";
import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import AccountPermissionSelect from "view/users/components/selects/AccountPermissionSelect";
import { fontAwesomeIcons } from "view/admin/utils/Icons";
import { AdminNavigationItem } from "view/admin/utils/admin.types";
import { adminNavigationItemSchema, AdminNavigationItemInput, AdminNavigationItemOutput } from "view/admin/utils/admin.schema";

import { NavigationUrlParameter } from "__generated__/enums";

interface NavigationItemDialogProps {
	open: boolean;
	navigationItem: AdminNavigationItem | undefined;
	onClose: () => void;
	onEdit: (data: AdminNavigationItem) => void;
	onAdd: (data: AdminNavigationItem) => void;
}

type FontAwesomeIconOption = {
	c: string;
	l: string;
	id: string;
	name: string;
};

const defaultNavigationItemValues: AdminNavigationItemInput = {
	children: [],
	languageKey: "",
	permissions: [],
	url: null,
	parameter: null,
	plugins: null,
	icon: null,
};

const NavigationCreateEditItemDialog = ({ open, navigationItem, onClose, onEdit, onAdd }: NavigationItemDialogProps) => {

	const { handleSubmit, control, reset, formState } = useForm<AdminNavigationItemInput, unknown, AdminNavigationItemOutput>({
		resolver: zodResolver(adminNavigationItemSchema),
		defaultValues: defaultNavigationItemValues,
	});

	const save = (data: AdminNavigationItemOutput) => {
		if (data.uniqueId) {
			onEdit(data);
		} else {
			onAdd(data);
		}
	};

	useEffect(() => {
		if (open && navigationItem) {
			reset({
				...navigationItem,
				children: navigationItem.children ?? null,
				url: navigationItem.url ?? null,
				parameter: navigationItem.parameter ?? null,
				plugins: navigationItem.plugins ?? null,
				icon: navigationItem.icon ?? null,
			});
		} else if (open) {
			reset(defaultNavigationItemValues);
		}
	}, [ open, navigationItem ]);

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			title={intl.formatMessage({ id: "ADMIN_NAVIGATION_ITEM_TITLE" })}
			form="adminNavigationItemForm"
			confirmText={intl.formatMessage({ id: "OK" })}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="adminNavigationItemForm">
				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_NAVIGATION_LANGUAGE_KEY_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="languageKey"
						control={control}
						render={({ field }) => (
							<React.Fragment>
								<Input {...field} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)}
					/>
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_NAVIGATION_USERS_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="permissions"
						control={control}
						render={({ field }) => (
							<React.Fragment>
								<AccountPermissionSelect {...field} multiple />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)}
					/>
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_NAVIGATION_HREF_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden">
					<Controller
						name="url"
						control={control}
						render={({ field }) => <Input {...field} value={field.value ?? ""} />} />
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_NAVIGATION_PARAMETER_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden">
					<Controller
						name="parameter"
						control={control}
						render={({ field }) => (
							<Select
								{...field}
								fetchOptions={Object.values(NavigationUrlParameter)}
								getOptionLabel={(option) => intl.formatMessage({ id: option })}
								disableSearch />
						)}
					/>
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_NAVIGATION_PLUGINS_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden">
					<Controller
						name="plugins"
						control={control}
						render={({ field }) => (
							<React.Fragment>
								<Input
									{...field}
									value={field.value?.join(", ") ?? ""}
									onChange={(event) => {
										const values = event.target.value.split(",").map(value => value.trim()).filter(value => value.length > 0);
										field.onChange(values.length > 0 ? values : null);
									}} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "ADMIN_NAVIGATION_FONT_AWESOME_LABEL" })}
					labelPlacement="left-center"
					overflow="hidden">
					<Controller
						name="icon"
						control={control}
						render={({ field }) => {
							const options: FontAwesomeIconOption[] = fontAwesomeIcons.map((icon) => ({ ...icon, id: icon.c, name: icon.l }));
							return (
								<Select
									value={field.value ? options.find(option => option.id === field.value) : undefined}
									fetchOptions={options}
									onChange={option => field.onChange(option?.id ?? null)}
									renderOption={(props, option) => (
										<li {...props}>
											<div>
												<span style={{ width: 40 }}>
													<i className={`fa ${convertedIcon(option.id)}`} style={{ marginRight: 8 }} />
												</span>
												{option.name}
											</div>
										</li>
									)} />
							);
						}} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default NavigationCreateEditItemDialog;
